import {
   trackInAnalyticsAddToCart,
   trackInAnalyticsRemoveFromCart,
   trackAnalyticsCartUpdate,
} from '@ifixit/analytics';
import { useAuthenticatedUser } from '@ifixit/auth-sdk';
import { useTranslations } from '@ifixit/i18n/hooks';
import { useShopifyStorefrontClient } from '@ifixit/shopify-storefront-client';
import { useIsMutating, useMutation, useQueryClient } from '@tanstack/react-query';
import { convertCartLineItemsToAnalyticsItem } from '../../helpers/analytics';
import { clearStoredCartId } from '../../helpers/storage';
import { getCart, isCartNotFoundError, updateCartLines } from '../../helpers/storefront-api';
import { buildIfixitCart } from '../../models/cart';
import { CartMutationType, type Cart, type CartLineItem } from '../../types';
import { useCart, useCartToasts } from './use-cart';
import { useCartKeys } from './use-cart-keys';
import { sendReactNativeMessage } from '@ifixit/utils';

export type UpdateLineItemQuantityInput = {
   line: CartLineItem;
   quantityDelta: number;
   analytics: {
      localeCode: string;
   };
};

export function useUpdateLineItemQuantity() {
   const { mutationKey, queryKey } = useCartKeys();
   const cart = useCart().data;
   const { data: user } = useAuthenticatedUser();
   const isMutating = useIsMutating({
      mutationKey,
      predicate: mutation => mutation.meta?.type !== CartMutationType.UpdateLineItemQuantity,
   });
   const queryClient = useQueryClient();
   const {
      client: storefrontClient,
      country,
      currencyCode,
      language,
      storeCode,
   } = useShopifyStorefrontClient();
   const t = useTranslations();
   const { showCartWarnings, showErrorToast } = useCartToasts({
      title: t('ErrorHandling.problemUpdatingQuantity'),
   });
   const mutation = useMutation({
      mutationKey,
      meta: { type: CartMutationType.UpdateLineItemQuantity },
      mutationFn: async ({ line, quantityDelta }: UpdateLineItemQuantityInput) => {
         if (cart == null || !cart.shopifyCartId || !line?.shopifyLineId) {
            return buildIfixitCart({
               cart: null,
               fallbackCurrencyCode: currencyCode,
               userErrors: [],
               validationErrors: [],
               warnings: [],
            });
         }
         const result = await updateCartLines(storefrontClient, {
            cartId: cart.shopifyCartId,
            country,
            lines: [{ id: line.shopifyLineId, quantity: line.quantity + quantityDelta }],
            language,
         });
         if (isCartNotFoundError(result.userErrors)) {
            clearStoredCartId({ storeCode, user: user ?? null });
            return buildIfixitCart({
               cart: null,
               fallbackCurrencyCode: currencyCode,
               userErrors: [],
               validationErrors: [],
               warnings: [],
            });
         }
         const newCart =
            result.cart ??
            (result.userErrors.length > 0 || result.validationErrors.length > 0
               ? await getCart(storefrontClient, {
                    cartId: cart.shopifyCartId,
                    country,
                    language,
                 })
               : null);
         return buildIfixitCart({
            cart: newCart,
            fallbackCurrencyCode: currencyCode,
            userErrors: result.userErrors,
            validationErrors: result.validationErrors,
            warnings: result.warnings,
         });
      },
      onMutate: async ({ line, quantityDelta }: UpdateLineItemQuantityInput) => {
         await queryClient.cancelQueries({ queryKey });

         const previousCart = queryClient.getQueryData<Cart>(queryKey);

         queryClient.setQueryData<Cart | undefined>(queryKey, current => {
            if (current == null) {
               return current;
            }
            const updatedItem = current.lineItems.find(item => item.itemcode === line.itemcode);
            if (updatedItem == null) {
               return current;
            }
            const updatedItemsCount = Math.max(current.totals.itemsCount + quantityDelta, 0);
            const updateTotalPrice = Math.max(
               Number(current.totals.price.amount) + quantityDelta * Number(line.price.amount),
               0
            ).toFixed(2);
            return {
               ...current,
               hasItemsInCart: updatedItemsCount > 0,
               lineItems: current.lineItems.map(lineItem => {
                  if (lineItem.itemcode === line.itemcode) {
                     const updatedQuantity = Math.max(lineItem.quantity + quantityDelta, 0);
                     return {
                        ...lineItem,
                        quantity: updatedQuantity,
                     };
                  }
                  return lineItem;
               }),
               totals: {
                  ...current.totals,
                  price: {
                     ...current.totals.price,
                     amount: updateTotalPrice,
                  },
                  itemsCount: updatedItemsCount,
               },
            };
         });

         return { previousCart };
      },
      onError: (error, variables, context) => {
         queryClient.setQueryData<Cart | undefined>(queryKey, context?.previousCart);
         showErrorToast();
      },
      onSuccess: (cart, variables, context) => {
         // Only update the cart state if we are the only mutation running.
         if (queryClient.isMutating({ mutationKey }) === 1) {
            queryClient.setQueryData<Cart | undefined>(queryKey, cart);
         }
         const { line, analytics } = variables;
         showCartWarnings({ cart, type: CartMutationType.UpdateLineItemQuantity, variables });

         const actualDelta = computeLineItemQuantityChange({
            previousCart: context.previousCart,
            currentCart: cart,
            itemcode: line.itemcode,
         });
         if (actualDelta !== 0) {
            const analyticsItems = convertCartLineItemsToAnalyticsItem([line]);
            const addOrRemoveAnalyticsItems = analyticsItems.map(item => ({
               ...item,
               quantity: Math.abs(actualDelta),
            }));
            const trackChangeInCart =
               actualDelta < 0 ? trackInAnalyticsRemoveFromCart : trackInAnalyticsAddToCart;
            trackChangeInCart({
               items: addOrRemoveAnalyticsItems,
               value: Number(line.price.amount),
               currency: line.price.currencyCode,
               localeCode: analytics.localeCode,
               shopifyCartId: cart.shopifyCartId,
            });
         }
         trackAnalyticsCartUpdate({
            items: convertCartLineItemsToAnalyticsItem(cart.lineItems),
            value: Number(cart.totals.price.amount),
            currency: cart.totals.price.currencyCode,
            localeCode: analytics.localeCode,
            shopifyCartId: cart.shopifyCartId,
         });
         sendReactNativeMessage({ type: 'cart_updated', total: cart.totals.itemsCount });
      },
   });
   return { updateLineItemQuantity: mutation, enabled: cart != null && !isMutating };
}

function computeLineItemQuantityChange({
   previousCart,
   currentCart,
   itemcode,
}: {
   previousCart: Cart | undefined;
   currentCart: Cart;
   itemcode: string;
}): number {
   const previousQuantity =
      previousCart?.lineItems.find(item => item.itemcode === itemcode)?.quantity ?? 0;
   const currentQuantity =
      currentCart.lineItems.find(item => item.itemcode === itemcode)?.quantity ?? 0;
   return currentQuantity - previousQuantity;
}
